/*
 * This helper encapculates Sentry methods.
 */
import { RuzukuGlobal, SessionUser } from 'server/types';
import * as Sentry from '@sentry/browser';
import { isRuzcalUrl } from 'shared/urls';

let isInited = false;
let routedPathname: string | undefined;

// Do not record request and response bodies for these paths
const PRIVATE_PATHS = ['auth', 'paypal-integration', 'zapier-integration'];

function shouldRecordSession(configuration: RuzukuGlobal, isRuzcalSession: boolean) {
  const { tenant, user } = configuration;
  // Record all ruzcal sessions even with no signed-in user
  if (isRuzcalSession) {
    return true;
  }

  if (!user) {
    return false;
  }

  // Record Replay sessions for paying guides on core tenants
  if (tenant.isCore) {
    return !user.isRestricted && user.tier !== 'free';
  }

  // Record all guide/admin users on non-core tenants
  return user.level !== 'student';
}

function shouldCreateSpanForRequest(url: string) {
  if (!Sentry.getActiveSpan()) {
    return false;
  }
  try {
    return new URL(url, location.href).origin === location.origin;
  } catch {
    return false;
  }
}

export function initSentry(configuration: RuzukuGlobal) {
  const { env, version, tenant } = configuration;
  if (env !== 'development' && !isInited) {
    const isRuzcalSession = isRuzcalUrl(window.location.pathname);

    Sentry.init({
      dsn: configuration.sentryDsn,
      environment: env,
      release: version,
      replaysSessionSampleRate: shouldRecordSession(configuration, isRuzcalSession) ? 1 : 0.0,
      // Record all sessions that throws an error
      replaysOnErrorSampleRate: 1.0,
      tracesSampleRate: 0.01,
      tracePropagationTargets: [],
      ignoreSpans: [{ op: /^resource\./ }],
      integrations: [
        Sentry.browserTracingIntegration({
          instrumentNavigation: false,
          shouldCreateSpanForRequest,
        }),
        Sentry.replayIntegration({
          stickySession: true,
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: false,
          // Mask texts with the following patterns
          mask: ['[data-private]'],
          // Ignore inputs with the following patterns
          ignore: ['[data-private]'],
          // We want to record all /rpx/* requests except `${PRIVATE_PATHS}`.
          // Sentry API is a bit weird for that case as they record the request
          // if it matches just one of the patterns in the array.
          // So we need to do it all in a single regex.
          networkDetailAllowUrls: [
            new RegExp(`^${window.location.origin}/rpx/(?!${PRIVATE_PATHS.join('|')}/)(.*)$`),
          ],
          // Extra headers to record
          networkResponseHeaders: ['cf-ray', 'cf-cache-status', 'rndr-id'],
        }),
      ],
    });
    const scope = Sentry.getCurrentScope();
    scope.setExtra('tenant', tenant.id);
    scope.setTag('coachmira', isRuzcalSession);
    isInited = true;
  }
}

export function nameSentryRoute(route: string) {
  if (!isInited) {
    return;
  }
  const name = `/${route}`;
  let span: Sentry.Span | undefined;
  if (routedPathname === undefined) {
    const activeSpan = Sentry.getActiveSpan();
    span = activeSpan && Sentry.getRootSpan(activeSpan);
  } else if (location.pathname !== routedPathname) {
    const client = Sentry.getClient();
    span = client && Sentry.startBrowserTracingNavigationSpan(client, { name });
  } else {
    return;
  }
  routedPathname = location.pathname;
  if (span) {
    Sentry.updateSpanName(span, name);
    span.setAttribute('sentry.segment.name.source', 'route');
  }
}

export function setSentryUser(user?: SessionUser) {
  Sentry.setUser(
    user
      ? {
          id: user.id,
          username: user.name,
          segment: user.level,
          timezone: user.timezone,
        }
      : null,
  );
}

export function setSentryExtra(key: string, value: string) {
  Sentry.getCurrentScope().setExtra(key, value);
}

export function captureException(err: Error, extra?: Record<string, unknown>) {
  Sentry.captureException(err, extra && { extra });
}

export function captureChunkLoadError(err: unknown, context: string) {
  Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
    tags: { chunk_load: context },
  });
}

export function captureWarning(
  message: string,
  opts: { tags?: Record<string, string>; extra?: Record<string, unknown> } = {},
) {
  Sentry.captureMessage(message, { level: 'warning', ...opts });
}
